import { useEffect, useState } from 'react';
import { Moon, Sun, MonitorSmartphone, ListChecks, CalendarClock } from 'lucide-react';
import BatchRunsView from './views/BatchRunsView.jsx';
import SchedulesView from './views/SchedulesView.jsx';
import { me, ApiError } from './api/client.js';
import Logo from './components/Logo.jsx';
import SignIn from './components/SignIn.jsx';

/**
 * Coquille de l'application.
 *
 * SPA sans router, comme les autres fronts de l'écosystème. Une seule vue pour
 * l'instant (la console des exécutions) ; le jour où d'autres s'ajoutent, la
 * navigation se fera par un `switch` sur un état, sans dépendance de routage.
 *
 * Synobatch n'a pas de backend : l'identité vient du cookie SSO posé par
 * synocore sur `.synotis.tech`, lu via `/internal/me`.
 */

// SPA sans router : la navigation est un simple `switch` sur un état, comme
// dans les autres fronts de l'écosystème.
const TABS = [
  { key: 'runs', label: 'Exécutions', Icon: ListChecks },
  { key: 'schedules', label: 'Planification', Icon: CalendarClock },
];

const THEMES = [
  { key: 'auto', label: 'Auto', Icon: MonitorSmartphone },
  { key: 'light', label: 'Clair', Icon: Sun },
  { key: 'dark', label: 'Sombre', Icon: Moon },
];

function applyTheme(theme) {
  document.documentElement.classList.remove('light', 'dark');
  if (theme === 'light') document.documentElement.classList.add('light');
  else if (theme === 'dark') document.documentElement.classList.add('dark');
  localStorage.setItem('hg_theme', theme);
}

export default function App() {
  const [theme, setTheme] = useState(() => localStorage.getItem('hg_theme') || 'auto');
  const [tab, setTab] = useState('runs');
  // Incrémenté après une relance : force l'historique à se recharger, pour que
  // l'exécution qu'on vient de déclencher y soit visible immédiatement.
  const [runsVersion, setRunsVersion] = useState(0);
  // undefined = vérification en cours · null = anonyme · objet = connecté
  const [user, setUser] = useState(undefined);
  // Session valide mais rôle insuffisant : se reconnecter n'y changerait rien,
  // le message doit donc être différent de celui d'un visiteur anonyme.
  const [forbidden, setForbidden] = useState(false);

  useEffect(() => applyTheme(theme), [theme]);

  useEffect(() => {
    const ctrl = new AbortController();
    me(ctrl.signal)
      .then(setUser)
      .catch((err) => {
        if (err?.name === 'AbortError') return;
        if (err instanceof ApiError && err.status === 403) setForbidden(true);
        setUser(null);
      });
    return () => ctrl.abort();
  }, []);

  // Tant que la session n'est pas tranchée, on n'affiche NI la console NI
  // l'écran de connexion : un flash de « connectez-vous » sur une session
  // valide serait déroutant.
  if (user === undefined) {
    return <div style={{ minHeight: '100vh', background: 'var(--bg)' }} />;
  }
  if (user === null) {
    return <SignIn reason={forbidden ? 'forbidden' : 'anonymous'} />;
  }
  // Authentifié mais sans le rôle : inutile de charger une console qui ne
  // renverrait que des 403 — on l'explique une fois, clairement.
  if (!user.isAdmin) {
    return <SignIn reason="forbidden" />;
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', color: 'var(--text)' }}>
      <header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          padding: '10px 20px',
          background: 'var(--nav-bg)',
          borderBottom: '1px solid var(--glass-border)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {/* Composant (pas <img>) pour que `currentColor` suive le thème. */}
          <Logo style={{ height: 34, width: 'auto', color: 'var(--text)' }} />
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Console des batchs</span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          {user && (
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
              {user.email || user.name}
            </span>
          )}
          <div style={{ display: 'flex', gap: 4 }}>
            {THEMES.map(({ key, label, Icon }) => (
              <button
                key={key}
                type="button"
                title={label}
                aria-label={label}
                onClick={() => setTheme(key)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: 6,
                  borderRadius: 'var(--radius-sm)',
                  border: `1px solid ${theme === key ? 'var(--brand)' : 'transparent'}`,
                  background: 'transparent',
                  color: theme === key ? 'var(--brand)' : 'var(--text-muted)',
                  cursor: 'pointer',
                }}
              >
                <Icon size={15} />
              </button>
            ))}
          </div>
        </div>
      </header>

      <nav
        style={{
          display: 'flex',
          gap: 4,
          padding: '0 20px',
          background: 'var(--nav-bg)',
          borderBottom: '1px solid var(--glass-border)',
        }}
      >
        {TABS.map(({ key, label, Icon }) => {
          const active = tab === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '10px 14px',
                border: 'none',
                borderBottom: `2px solid ${active ? 'var(--brand)' : 'transparent'}`,
                background: 'transparent',
                color: active ? 'var(--text)' : 'var(--text-muted)',
                fontSize: 13.5,
                fontWeight: active ? 700 : 500,
                cursor: 'pointer',
              }}
            >
              <Icon size={15} /> {label}
            </button>
          );
        })}
      </nav>

      <main style={{ maxWidth: 1240, margin: '0 auto', padding: '20px 16px 48px' }}>
        {tab === 'runs' ? (
          <BatchRunsView key={runsVersion} />
        ) : (
          <SchedulesView
            onRunFinished={() => {
              setRunsVersion((v) => v + 1);
              setTab('runs');
            }}
          />
        )}
      </main>

      <footer
        style={{
          textAlign: 'center',
          padding: '16px',
          fontSize: 11.5,
          color: 'var(--text-muted)',
          borderTop: '1px solid var(--glass-border)',
        }}
      >
        Écosystème Synotis · les données proviennent du journal unifié de synocore
      </footer>
    </div>
  );
}
